import { useAuthStore } from '../store/authStore';
import { useUserPresenceStore } from '../store/userPresenceStore';
import { useUserStatusStore } from '../store/userStatusStore';
import { resolveStatus } from '../utils/presenceStatus';

/**
 * The status badge for `userId`: `'online' | 'away' | 'busy' | 'invisible' |
 * 'offline' | null`.
 *
 * `null` means UNKNOWN and renders nothing — a foreign user we have not been
 * told about yet (before the first `presence:sync`), one the server declined to
 * report (not entitled), or the free/local-only plan, which has no socket and
 * where this store stays empty forever. `'offline'` means the server told us
 * nobody is connected. The two are never conflated: painting an unknown user
 * grey is a confident lie, and on a free account it would be a permanent one.
 *
 * ── One source, one selector ────────────────────────────────────────────────
 * Foreign presence comes from `userPresenceStore` and from nowhere else. This
 * hook used to first-match across `chatStore.presenceByChannel`,
 * `presenceStore.rawState` and `teamPresenceStore.byTeam`; those rosters cover
 * a subset of the people you can see, so "absent from all three" never meant
 * offline. Both stores still exist and still back `selectRequestViewers` and
 * the workspace roster UI — this hook simply stopped reading them.
 *
 * ── Re-render contract ──────────────────────────────────────────────────────
 * EVERY selector here returns a STRING or `null`, never an object or array, so
 * zustand's `Object.is` suppresses the render unless this user's own status
 * changed. Selecting `s.byUserId` itself — even "just to read one key out of it
 * afterwards" — re-renders every mounted avatar on every presence ping and the
 * contract is gone. `presenceRerenderContract.test.js` counts renders and fails
 * if it is.
 */
export default function usePresenceStatus(userId) {
  const selfId = useAuthStore((s) => s.user?.id);
  const selfStatus = useUserStatusStore((s) => s.status);
  const foreignStatus = useUserPresenceStore((s) => s.byUserId[String(userId)] ?? null);

  return resolveStatus({
    userId,
    selfId,
    selfStatus,
    // `isPresent` reads as "we know something about this user" — an absent key
    // is unknown, not offline.
    isPresent: foreignStatus !== null,
    presenceMeta: foreignStatus ? { status: foreignStatus } : null,
  });
}
